import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Login } from 'Ui/Components/Login'
import { ihm_viewer__dictionary, locale } from 'Application/i18n/i18nViewer'
import { getUser, logout } from 'Infrastructure/Service/AuthService'
import { PutProjectStatus } from 'Infrastructure/Service/ProjectService'
import { PatientResumeCompact } from 'Ui/Layer/components/information/PatientResumeCompact'
import { CompanyLogoIcon } from 'Ui/Layer/components/information/CompanyLogoIcon'
import { HeaderAccountBlock } from 'Ui/Components/HeaderAccountBlock'

/*****************************************************************
 * Header
 * ***************************************************************/


export const getUserName = async () => {
    try {
        const response = await getUser();
        if (!response.ok) {
            return null;
        }
        const name = await response.json();
        return name;
    } catch (error) {
        console.error('Error fetching user name:', error);
        return null;
    }
};

export const Header = props => {

    const
        [action, setAction] = useState(null),
        [connectionForm, setConnectionForm] = useState(false),
        [userName, setUsername] = useState(''),
        dispatch = useDispatch(),
        { UserReducer, ViewportReducer, AnimationReducer } = useSelector((state) => state),
        isEditMode = ViewportReducer.mode === "edition",
        deniedServices = useSelector((state) => state.UserReducer.deniedServices),
        isSceneLoaded = useSelector((state) => state.AnimationReducer.loaded),
        connectionHandler = connected => {
            if (connected === false) {
                setConnectionForm(true)
            } else {
                // Appeler le serveur pour supprimer le cookie HttpOnly
                logout().then(success => {
                    if (success) {
                        dispatch({ type: "USER_DISCONNECTED", payload: false })
                        setConnectionForm(false)
                    } else {
                        console.error('Logout failed')
                    }
                })
            }
        },
        setStatus = actionRequest => {
            const actionMatrix = {
                valid: 'VALIDATED',
                refuse: 'REFUSED'
            }
            if (!Object.values(actionMatrix).includes(action)) {
                setAction(actionMatrix[actionRequest])
                PutProjectStatus(props.project.ref.sk, actionRequest)
            }
        }

    const getAnonymous = (name = '') => name.split(' ').map(n => n[0] + 'xxxx').join('');
    const hasCaseData = !!AnimationReducer.metas?.caseData;
    const isTitleBlockDenied = deniedServices.includes('titleBlock');
    const shouldShowCompact = !isTitleBlockDenied && hasCaseData;
    const isValidationDenied = deniedServices.includes('validation');
    const isConnectionDenied = deniedServices.includes('connection');
    const isNotificationDenied = deniedServices.includes('notification');
    const isPwaDenied = deniedServices.includes('pwa');
    const headerIsHidden = !isSceneLoaded;
    const shouldShowValidation = !isValidationDenied;
    const shouldShowConnection = !isConnectionDenied;
    const shouldShowNotification = !isNotificationDenied;
    const shouldShowPwa = !isPwaDenied;
    const isUserConnected = UserReducer.connected;
    const displayUserName = userName;
    const displayFirstName = (displayUserName ?? '').trim().split(/\s+/)[0] ?? '';
    const isMobile = useSelector(state => {
        const g = state.LayoutReducer?.global
        return g ? (g.isPortrait || g.isMobileLandscape) : false
    })
    const displayAction = action;
    const validationButtonClass = displayAction === 'VALIDATED' ? 'validated' : 'action-pending';
    const refusalButtonClass = displayAction === 'REFUSED' ? 'refused' : 'action-pending';
    const connectionButtonLabel = isUserConnected
        ? ihm_viewer__dictionary.LOGIN.LOGOUT[locale()]
        : ihm_viewer__dictionary.LOGIN.LOGIN[locale()];
    const shouldShowLoginForm = connectionForm;
    const shouldShowDashboardLink = isUserConnected;

    const hasProjectRef = !!props.project?.ref?.sk;
    const hasCaseDataId = !!AnimationReducer.metas?.caseData?.caseID;
    const logoUrl = hasProjectRef
        ? `${process.env.REACT_APP_U1_VIEWERSVC_API}project/${props.project.ref.sk}/logo?dark=true`
        : hasCaseDataId
            ? `${process.env.REACT_APP_U1_VIEWERSVC_API}project/${AnimationReducer.metas.caseData.caseID}/logo?dark=true`
            : `${process.env.REACT_APP_U1_VIEWERSVC_API}company/logo?dark=true`;

    useEffect(() => {
        if (isUserConnected) {
            getUserName()
                .then(name => {
                    if (name) {
                        setUsername(name);
                        dispatch({ type: "ADD_USER_NAME", payload: name });
                    }
                });
        }
        if (props.project.ref) {
            setAction(props.project.ref.Status.designation.toUpperCase());
        }
    }, [isUserConnected, props.project.ref]);


    const headerClass = headerIsHidden ? 'hidden' : '';

    return (
        <header id="HeaderTop" className={headerClass}>
            <div>
                <div className="header-left portrait-only-logo">
                    <CompanyLogoIcon logoUrl={logoUrl} deniedServices={deniedServices} />
                </div>

                {shouldShowCompact && (
                    <PatientResumeCompact
                        metas={AnimationReducer.metas}
                        isConnected={isUserConnected}
                        getAnonymous={getAnonymous}
                        className={isEditMode ? 'edition-visible' : ''}
                    />
                )}

                <div className="header-right">
                    {isEditMode ? (
                        <span className="header-btn edition-mode-label">
                            {ihm_viewer__dictionary.EDITOR_MODE_ACTIVE[locale()]}
                        </span>
                    ) : (
                        <>
                            {shouldShowValidation && (
                                    <section id="validationTab" className="hide-mobile">
                                    <button
                                        className={`header-btn ${validationButtonClass}`}
                                        onClick={() => setStatus('valid')}
                                    >
                                        {ihm_viewer__dictionary.VALIDATION.ACTION.VALIDATE[locale()]}
                                    </button>
                                    <button
                                        className={`header-btn ${refusalButtonClass}`}
                                        onClick={() => setStatus('refuse')}
                                    >
                                        {ihm_viewer__dictionary.VALIDATION.ACTION.REFUSE[locale()]}
                                    </button>
                                </section>
                            )}

                            {shouldShowConnection && (
                                <section id="accountTabs">
                                    <HeaderAccountBlock
                                        isPortrait={props.isPortrait}
                                        isMobile={isMobile}
                                        isUserConnected={isUserConnected}
                                        shouldShowNotification={shouldShowNotification}
                                        shouldShowPwa={shouldShowPwa}
                                        shouldShowDashboardLink={shouldShowDashboardLink}
                                        displayFirstName={displayFirstName}
                                        displayUserName={displayUserName}
                                        connectionButtonLabel={connectionButtonLabel}
                                        connectionHandler={() => connectionHandler(UserReducer.connected)}
                                    />
                                    {shouldShowLoginForm && <Login setConnectionForm={setConnectionForm} />}
                                </section>
                            )}
                        </>
                    )}
                </div>
            </div>
        </header>
    )
}
